/**
 * Organization Managed Billing Card
 *
 * Whether JotPsych's billing team works this organization's claims. Managed
 * organizations make up the Morning Worklist's default view and the agent's
 * managed list. The setting is independent of the JotBill take: an
 * organization can be managed with no participation fee.
 */

import { type AutosaveState, AutosaveStatus } from '@components/AutosaveStatus';
import { SupportAgent as SupportAgentIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

type BillingService = 'managed' | 'self_serve';

export interface OrganizationManagedBillingCardProps {
  /** Undefined until the setting has loaded */
  managed: boolean | undefined;
  updatedAt?: string | null;
  updatedBy?: string | null;
  isLoading: boolean;
  /** The setting could not be loaded */
  loadFailed?: boolean;
  onRetry?: () => void;
  saveStatus: AutosaveState;
  onChange: (managed: boolean) => void;
}

const DESCRIPTIONS: Record<BillingService, string> = {
  managed:
    "JotPsych's billing team works this organization's claims. It is on the Morning Worklist's default view.",
  self_serve:
    'The practice bills for itself. It is on the Morning Worklist only under All organizations.',
};

export function OrganizationManagedBillingCard({
  managed,
  updatedAt,
  updatedBy,
  isLoading,
  loadFailed = false,
  onRetry,
  saveStatus,
  onChange,
}: OrganizationManagedBillingCardProps) {
  const theme = useTheme();
  const value: BillingService | null =
    managed === undefined ? null : managed ? 'managed' : 'self_serve';

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SupportAgentIcon color={managed ? 'success' : 'disabled'} sx={{ mr: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Billing service
            </Typography>
          </Box>
          <AutosaveStatus status={saveStatus} isDirty={false} />
        </Box>

        {loadFailed ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="error">
              Could not load the billing service.
            </Typography>
            <Button size="small" onClick={onRetry}>
              Retry
            </Button>
          </Box>
        ) : isLoading || value === null ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={value}
              disabled={saveStatus === 'saving'}
              // MUI passes null when the active segment is re-clicked; keep one on.
              onChange={(_, next: BillingService | null) => {
                if (next && next !== value) onChange(next === 'managed');
              }}
              aria-label="Billing service"
            >
              <ToggleButton value="managed">Managed</ToggleButton>
              <ToggleButton value="self_serve">Self-serve</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {DESCRIPTIONS[value]}
            </Typography>
            {updatedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Last updated {new Date(updatedAt).toLocaleString()}
                {updatedBy ? ` by ${updatedBy}` : ''}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
